import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Container,
  ListItemSecondaryAction,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import ListItemIcon from "@mui/material/ListItemIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { API, graphqlOperation } from "aws-amplify";
import { listUserGroups } from "../graphql/queries";
import { createUserGroup, deleteUserGroup, updateUserGroup } from "../graphql/mutations";
import { useUser } from "../UserContext";

const UserGroupManager = () => {
  const [userGroups, setUserGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [newPercentageAdjustment, setNewPercentageAdjustment] = useState("");
  const [newDollarAdjustment, setNewDollarAdjustment] = useState("");
  const [newAdjustmentType, setNewAdjustmentType] = useState("percentage");
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { userRecord } = useUser();

  useEffect(() => {
    const fetchUserGroups = async () => {
      try {
        console.log("Fetching user groups for user:", userRecord?.id);
        const result = await API.graphql(graphqlOperation(listUserGroups, {
          filter: {
            userID: { eq: userRecord.id }
          }
        }));
        console.log("UserGroups API result:", result);
        setUserGroups(result.data.listUserGroups.items);
      } catch (error) {
        console.error("Error fetching user groups:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
      }
    };

    if (userRecord?.id) {
      fetchUserGroups();
    }
  }, [userRecord]);

  const resetForm = () => {
    setNewGroupName("");
    setNewPercentageAdjustment("");
    setNewDollarAdjustment("");
    setNewAdjustmentType("percentage");
  };

  const handleAddGroup = async () => {
    if (newGroupName.trim() === "") {
      setSnackbarMessage("Group name cannot be empty.");
      setSnackbarOpen(true);
      return;
    }

    try {
      const input = { 
        title: newGroupName, 
        content: "",
        userID: userRecord.id,
        percentageAdjustment: parseFloat(newPercentageAdjustment) || 0,
        dollarAdjustment: parseFloat(newDollarAdjustment) || 0,
        adjustmentType: newAdjustmentType
      };
      
      console.log("Creating user group with input:", input);
      const result = await API.graphql(
        graphqlOperation(createUserGroup, { input })
      );
      console.log("Create user group result:", result);
      
      setUserGroups([...userGroups, result.data.createUserGroup]);
      resetForm();
      setSnackbarMessage(`Group "${newGroupName}" added successfully with pricing adjustments.`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error adding group:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      setSnackbarMessage("Failed to add group. Check console for details.");
      setSnackbarOpen(true);
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setNewGroupName(group.title);
    setNewPercentageAdjustment(group.percentageAdjustment || "");
    setNewDollarAdjustment(group.dollarAdjustment || "");
    setNewAdjustmentType(group.adjustmentType || "percentage");
    setEditDialogOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;

    try {
      const input = {
        id: editingGroup.id,
        title: newGroupName,
        content: editingGroup.content || "",
        percentageAdjustment: parseFloat(newPercentageAdjustment) || 0,
        dollarAdjustment: parseFloat(newDollarAdjustment) || 0,
        adjustmentType: newAdjustmentType
      };

      console.log("Updating user group with input:", input);
      const result = await API.graphql(
        graphqlOperation(updateUserGroup, { input })
      );
      console.log("Update user group result:", result);

      const updatedGroup = result.data.updateUserGroup;
      setUserGroups(userGroups.map(group => 
        group.id === updatedGroup.id ? updatedGroup : group
      ));

      setEditDialogOpen(false);
      setEditingGroup(null);
      resetForm();
      setSnackbarMessage(`Group "${updatedGroup.title}" updated successfully.`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating group:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      setSnackbarMessage("Failed to update group. Check console for details.");
      setSnackbarOpen(true);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      const input = { id: deleteGroupId };
      await API.graphql(graphqlOperation(deleteUserGroup, { input }));
      setUserGroups(userGroups.filter((group) => group.id !== deleteGroupId));
      setSnackbarMessage(`Group deleted successfully.`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting group:", error);
      setSnackbarMessage("Failed to delete group.");
      setSnackbarOpen(true);
    } finally {
      setDialogOpen(false);
    }
  };

  const openDeleteDialog = (groupId) => {
    setDeleteGroupId(groupId);
    setDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDialogOpen(false);
    setDeleteGroupId(null);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingGroup(null);
    resetForm();
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
  };

  const formatAdjustment = (group) => {
    const hasPercentage = group.percentageAdjustment && group.percentageAdjustment !== 0;
    const hasDollar = group.dollarAdjustment && group.dollarAdjustment !== 0;
    
    if (!hasPercentage && !hasDollar) {
      return "No pricing adjustments";
    }
    
    const parts = [];
    if (hasPercentage) {
      parts.push(`${group.percentageAdjustment > 0 ? '+' : ''}${group.percentageAdjustment}%`);
    }
    if (hasDollar) {
      parts.push(`${group.dollarAdjustment > 0 ? '+' : ''}$${group.dollarAdjustment}`);
    }
    
    return parts.join(' ');
  };

  const PricingForm = () => (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
          Pricing Adjustments (Optional)
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Percentage Adjustment"
          variant="outlined"
          fullWidth
          value={newPercentageAdjustment}
          onChange={(e) => setNewPercentageAdjustment(e.target.value)}
          placeholder="e.g. 10 for +10%, -5 for -5%"
          type="number"
          helperText="Percentage to adjust prices (positive for increase, negative for decrease)"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Dollar Adjustment"
          variant="outlined"
          fullWidth
          value={newDollarAdjustment}
          onChange={(e) => setNewDollarAdjustment(e.target.value)}
          placeholder="e.g. 50 for +$50, -25 for -$25"
          type="number"
          helperText="Fixed dollar amount to adjust prices"
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Adjustment Application</InputLabel>
          <Select
            value={newAdjustmentType}
            label="Adjustment Application"
            onChange={(e) => setNewAdjustmentType(e.target.value)}
          >
            <MenuItem value="percentage">Apply percentage first, then dollar amount</MenuItem>
            <MenuItem value="dollar">Apply dollar amount first, then percentage</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4, textAlign: "center", pt: 3 }}>
        <Typography variant="h4" gutterBottom>
          Manage User Groups
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          User groups allow administrators to efficiently manage and distribute pricelists to multiple users simultaneously.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create new groups, delete existing ones, and organize your users for easier pricelist distribution.
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <AddIcon sx={{ mr: 1 }} />
          Create New User Group
        </Typography>
        
        <Box sx={{ display: "flex", mb: 2, alignItems: "center" }}>
          <TextField
            label="Group Name"
            variant="outlined"
            fullWidth
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            sx={{ mr: 2 }}
            placeholder="e.g. Premium Customers, Wholesale Partners"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddGroup}
            sx={{ minWidth: 120 }}
          >
            Add Group
          </Button>
        </Box>

        <PricingForm />
      </Paper>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <GroupIcon sx={{ mr: 1 }} />
          Existing User Groups ({userGroups.length})
        </Typography>
        
        {userGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No user groups created yet. Create your first group above.
          </Typography>
        ) : (
          <List>
            {userGroups.map((group) => (
              <ListItem
                key={group.id}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  '&:last-child': { mb: 0 }
                }}
              >
                <ListItemIcon>
                  <GroupIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {group.title}
                      </Typography>
                      {(group.percentageAdjustment !== 0 || group.dollarAdjustment !== 0) && (
                        <Chip 
                          icon={<MonetizationOnIcon />}
                          label={formatAdjustment(group)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Group ID: {group.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Created: {new Date(group.createdAt).toLocaleDateString()}
                        {group.updatedAt !== group.createdAt && (
                          <span> • Updated: {new Date(group.updatedAt).toLocaleDateString()}</span>
                        )}
                      </Typography>
                      {(group.percentageAdjustment !== 0 || group.dollarAdjustment !== 0) && (
                        <Typography variant="caption" display="block" color="primary">
                          Pricing: {formatAdjustment(group)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => handleEditGroup(group)}
                    sx={{ mr: 1, color: 'primary.main' }}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => openDeleteDialog(group.id)}
                    sx={{ color: 'error.main' }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Edit Group Dialog - Full Version with Pricing */}
      <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit User Group</DialogTitle>
        <DialogContent>
          <TextField
            label="Group Name"
            variant="outlined"
            fullWidth
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="e.g. Premium Customers, Wholesale Partners"
          />
          
          {/* Add pricing adjustments to edit dialog */}
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 3 }}>
            Pricing Adjustments (Optional)
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Percentage Adjustment"
                variant="outlined"
                fullWidth
                value={newPercentageAdjustment}
                onChange={(e) => setNewPercentageAdjustment(e.target.value)}
                placeholder="e.g. 10 for +10%, -5 for -5%"
                type="number"
                helperText="Percentage to adjust prices (positive for increase, negative for decrease)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Dollar Adjustment"
                variant="outlined"
                fullWidth
                value={newDollarAdjustment}
                onChange={(e) => setNewDollarAdjustment(e.target.value)}
                placeholder="e.g. 50 for +$50, -25 for -$25"
                type="number"
                helperText="Fixed dollar amount to adjust prices"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Adjustment Application</InputLabel>
                <Select
                  value={newAdjustmentType}
                  label="Adjustment Application"
                  onChange={(e) => setNewAdjustmentType(e.target.value)}
                >
                  <MenuItem value="percentage">Apply percentage first, then dollar amount</MenuItem>
                  <MenuItem value="dollar">Apply dollar amount first, then percentage</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateGroup} 
            color="primary" 
            variant="contained"
            disabled={!newGroupName.trim()}
          >
            Update Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete User Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user group? This action cannot
            be undone and may affect pricelist distribution and pricing for associated users.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteGroup} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity="info" sx={{ width: "100%" }} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserGroupManager;
